import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, ChevronRight, Mail, Sparkles, Lightbulb,
  ShieldCheck, Info, RefreshCw,
} from "lucide-react";
import Topbar from "./Topbar";
import "../quiz.css";
import "../audit.css";
import {
  AUDIT_INTRO, BELIEFS, RATING_LABELS, bandFor, bucketForBelief,
} from "../lib/auditData";
import useRouteMeta from "../lib/useRouteMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESET_CTA_URL =
  process.env.REACT_APP_RESET_CTA_URL ||
  "https://finlightenment.mykajabi.com/Financial-foundation-reset-priority-list";

// Stages: 'intro' -> 'belief' (per index 0..4) -> 'gate' -> 'results'
export default function MoneyBeliefAudit() {
  useRouteMeta("audit");
  const [stage, setStage] = useState("intro");
  const [bIndex, setBIndex] = useState(0);
  const [ratings, setRatings] = useState({}); // { scarcity: [3,4,2,1], ... }
  const [submitting, setSubmitting] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Persist progress so refresh doesn't lose work
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("fa_state") || "null");
      if (stored?.ratings) setRatings(stored.ratings);
      if (Number.isInteger(stored?.bIndex)) setBIndex(stored.bIndex);
      if (stored?.stage && ["intro", "belief"].includes(stored.stage)) setStage(stored.stage);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (stage === "intro" || stage === "belief") {
      try {
        localStorage.setItem("fa_state", JSON.stringify({ stage, ratings, bIndex }));
      } catch (e) { /* ignore */ }
    }
  }, [stage, ratings, bIndex]);

  const belief = BELIEFS[bIndex];
  const currentRatings = ratings[belief?.key] || [];
  const allFour = currentRatings.length === 4 && currentRatings.every((v) => v >= 1 && v <= 5);
  const sectionScore = allFour ? currentRatings.reduce((a, b) => a + b, 0) : 0;
  const sectionBand = allFour ? bandFor(belief, sectionScore) : null;
  const sectionBucket = allFour ? bucketForBelief(sectionScore) : null;

  const allDone = BELIEFS.every((b) => {
    const r = ratings[b.key] || [];
    return r.length === 4 && r.every((v) => v >= 1 && v <= 5);
  });

  const setRating = (statementIdx, value) => {
    setRatings((prev) => {
      const list = [...(prev[belief.key] || [])];
      list[statementIdx] = value;
      return { ...prev, [belief.key]: list };
    });
  };

  const startAudit = () => { setStage("belief"); setBIndex(0); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const goNext = () => {
    if (bIndex < BELIEFS.length - 1) {
      setBIndex(bIndex + 1);
    } else {
      setStage("gate");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (bIndex > 0) setBIndex(bIndex - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateLead = (k) => (e) => {
    setLead((p) => ({ ...p, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validateGate = () => {
    const er = {};
    if (!lead.name.trim()) er.name = "Please enter your name.";
    if (!lead.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email))
      er.email = "Please enter a valid email.";
    if (!lead.phone.trim()) er.phone = "Please enter your phone number.";
    else if (lead.phone.replace(/\D/g, "").length < 7)
      er.phone = "Please enter a valid phone number.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submitAudit = async (e) => {
    e?.preventDefault();
    if (!validateGate() || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/audit/submit`, {
        name: lead.name.trim(),
        email: lead.email.trim().toLowerCase(),
        phone: lead.phone.trim(),
        ratings,
      });
      setServerResult(res.data);
      setStage("results");
      try { localStorage.removeItem("fa_state"); } catch (e) { /* ignore */ }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setToast({ msg: detail, kind: "error" });
      setSubmitting(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  const restart = () => {
    setRatings({}); setBIndex(0); setLead({ name: "", email: "", phone: "" });
    setServerResult(null); setErrors({}); setStage("intro");
    try { localStorage.removeItem("fa_state"); } catch (e) { /* ignore */ }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="fq-app" data-testid="fa-app">
      <Topbar />
      <div className="fq-wrap">
        {stage === "intro" && <IntroCard onStart={startAudit} />}

        {stage === "belief" && belief && (
          <BeliefCard
            belief={belief}
            bIndex={bIndex}
            total={BELIEFS.length}
            currentRatings={currentRatings}
            allFour={allFour}
            sectionScore={sectionScore}
            sectionBand={sectionBand}
            sectionBucket={sectionBucket}
            onRate={setRating}
            onNext={goNext}
            onBack={goBack}
          />
        )}

        {stage === "gate" && (
          <GateCard
            lead={lead}
            errors={errors}
            submitting={submitting}
            onChange={updateLead}
            onSubmit={submitAudit}
            onBack={() => { setStage("belief"); setBIndex(BELIEFS.length - 1); }}
            allDone={allDone}
          />
        )}

        {stage === "results" && serverResult && (
          <ResultsView result={serverResult} lead={lead} onRestart={restart} />
        )}
      </div>

      {toast && (
        <div className={`fq-toast ${toast.kind === "error" ? "error" : ""}`} data-testid="fa-toast">
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ====================================================== */
/*                       Intro                            */
/* ====================================================== */
function IntroCard({ onStart }) {
  return (
    <div className="fq-card fq-fade-in" data-testid="fa-intro">
      <span className="fq-eyebrow">{AUDIT_INTRO.badge}</span>
      <h1 className="fq-headline">{AUDIT_INTRO.headline}</h1>
      <p className="fq-sub">{AUDIT_INTRO.subhead}</p>

      {AUDIT_INTRO.body.map((para, i) => (
        <p key={i} className="fq-sub">{para}</p>
      ))}

      <div className="fq-pillars" style={{ gridTemplateColumns: "repeat(2, 1fr)" }} data-testid="fa-belief-list">
        {AUDIT_INTRO.pillars.map((p, i) => (
          <div className="fq-pillar-chip" key={p}>
            <span style={{ color: "var(--gold)", marginRight: 6, fontFamily: "'Fraunces', serif", fontWeight: 700 }}>{i + 1}.</span>
            {p}
          </div>
        ))}
      </div>

      <p className="fq-sub" style={{ marginBottom: 28 }}><em>{AUDIT_INTRO.closing}</em></p>

      <div className="fq-cta-row">
        <button className="fq-btn" onClick={onStart} data-testid="fa-start">
          Begin the audit <ArrowRight size={18} />
        </button>
        <span className="fq-trust">5 sections · ~6 minutes · Free results</span>
      </div>
    </div>
  );
}

/* ====================================================== */
/*                Belief section card                     */
/* ====================================================== */
function BeliefCard({ belief, bIndex, total, currentRatings, allFour, sectionScore, sectionBand, sectionBucket, onRate, onNext, onBack }) {
  const pct = ((bIndex + 1) / total) * 100;
  const isLast = bIndex === total - 1;

  return (
    <div className="fq-card fq-fade-in" data-testid="fa-belief-card">
      <div className="fq-progress-row">
        <span data-testid="fa-progress-text">Belief {bIndex + 1} of {total}</span>
        <span>{Math.round(pct)}% complete</span>
      </div>
      <div className="fq-progress-track">
        <div className="fq-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="fq-belief-head">
        <div className="fq-belief-num">
          <span className="num-circle">{belief.id}</span>
          BELIEF #{belief.id}
        </div>
        <h2 className="fq-belief-title" data-testid="fa-belief-title">{belief.title}</h2>
        <p className="fq-belief-tagline">{belief.tagline}</p>
      </div>

      <p className="fq-sub" style={{ fontSize: 14.5, marginBottom: 18 }}>
        For each statement, rate how strongly you agree on a scale of <strong>1 = Never True</strong> to <strong>5 = Always True</strong>.
      </p>

      <div className="fq-stmt-list">
        {belief.statements.map((stmt, i) => {
          const value = currentRatings[i];
          return (
            <div className={`fq-stmt ${value ? "answered" : ""}`} key={i} data-testid={`fa-stmt-${belief.key}-${i}`}>
              <p className="fq-stmt-text">{stmt}</p>
              <div className="fq-rating">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`fq-rating-btn ${value === v ? "selected" : ""}`}
                    onClick={() => onRate(i, v)}
                    data-testid={`fa-rate-${belief.key}-${i}-${v}`}
                    aria-label={`Rate ${v} — ${RATING_LABELS[v - 1]}`}
                  >
                    <span className="fq-rating-num">{v}</span>
                    <span className="fq-rating-label">{RATING_LABELS[v - 1]}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Section result reveal once all 4 are answered */}
      {allFour && (
        <div className="fq-belief-result" data-testid="fa-belief-result">
          <div className="fq-belief-result-head">
            <div className={`fq-belief-result-pill bucket-${sectionBucket}`}>
              {sectionBucket === 0 && "Strong foundation"}
              {sectionBucket === 1 && "In transition"}
              {sectionBucket === 2 && "Limiting belief"}
            </div>
            <div className="fq-belief-result-score">
              {sectionScore}<span className="fq-belief-result-score-max">/20</span>
            </div>
          </div>
          <h3 className="fq-belief-result-label">{sectionBand.label}</h3>
          {sectionBand.interp && <p className="fq-belief-result-interp">{sectionBand.interp}</p>}
          <div className="fq-reframe-preview">
            <Lightbulb size={16} strokeWidth={2.5} />
            <span>Your full reframe + weekly practice for this belief is included in your final results.</span>
          </div>
        </div>
      )}

      <div className="fq-actions" style={{ marginTop: 28 }}>
        <button className="fq-back" onClick={onBack} disabled={bIndex === 0} data-testid="fa-back">
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="button"
          className="fq-btn fq-btn-gold"
          onClick={onNext}
          disabled={!allFour}
          data-testid="fa-next"
          style={{ width: "auto", padding: "12px 22px", fontSize: 14 }}
        >
          {isLast ? "Continue to results" : "Next belief"} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ====================================================== */
/*                      Email gate                        */
/* ====================================================== */
function GateCard({ lead, errors, submitting, onChange, onSubmit, onBack, allDone }) {
  return (
    <div className="fq-card fq-fade-in fq-gate" data-testid="fa-gate">
      <div className="fq-gate-icon"><Sparkles size={28} strokeWidth={2.5} /></div>
      <h2 className="fq-gate-headline">Your Money Mindset Profile is ready.</h2>
      <p className="fq-gate-sub">
        Tell us where to send your <strong>full personalized PDF</strong> — including all five belief scores,
        your primary block, and the reframe exercises designed to start rewiring it. We'll show your profile instantly.
      </p>

      <form className="fq-form" onSubmit={onSubmit} noValidate>
        <div className="fq-form-group">
          <label htmlFor="fa-name">Full name</label>
          <input id="fa-name" type="text" className={`fq-input ${errors.name ? "error" : ""}`}
            placeholder="Jane Smith" value={lead.name} onChange={onChange("name")} autoComplete="name" data-testid="fa-input-name" />
          {errors.name && <div className="fq-form-error">{errors.name}</div>}
        </div>

        <div className="fq-form-group">
          <label htmlFor="fa-email">Email address</label>
          <input id="fa-email" type="email" className={`fq-input ${errors.email ? "error" : ""}`}
            placeholder="you@example.com" value={lead.email} onChange={onChange("email")} autoComplete="email" data-testid="fa-input-email" />
          {errors.email && <div className="fq-form-error">{errors.email}</div>}
        </div>

        <div className="fq-form-group">
          <label htmlFor="fa-phone">Phone number</label>
          <input id="fa-phone" type="tel" className={`fq-input ${errors.phone ? "error" : ""}`}
            placeholder="(555) 123-4567" value={lead.phone} onChange={onChange("phone")} autoComplete="tel" data-testid="fa-input-phone" />
          {errors.phone && <div className="fq-form-error">{errors.phone}</div>}
        </div>

        <button type="submit" className="fq-btn" disabled={submitting || !allDone}
          data-testid="fa-submit" style={{ marginTop: 6 }}>
          {submitting ? "Building your profile…" : (<>Reveal my profile <ChevronRight size={18} /></>)}
        </button>

        <div className="fq-form-trust">
          We respect your privacy. By signing up you agree to receive emails from Finlightenment.
          You can unsubscribe anytime.
        </div>
      </form>

      <div style={{ marginTop: 22 }}>
        <button className="fq-back" onClick={onBack} type="button" data-testid="fa-gate-back">
          <ArrowLeft size={16} /> Review last belief
        </button>
      </div>
    </div>
  );
}

/* ====================================================== */
/*                      Results view                      */
/* ====================================================== */
function ResultsView({ result, lead, onRestart }) {
  const primaryKey = result.primary_block?.key;

  // Order breakdown so primary block comes first in reframes (most useful)
  const orderedReframes = useMemo(() => {
    const items = [...(result.breakdown || [])];
    items.sort((a, b) => (a.key === primaryKey ? -1 : b.key === primaryKey ? 1 : b.score - a.score));
    return items;
  }, [result.breakdown, primaryKey]);

  return (
    <div className="fq-fade-in" data-testid="fa-results">
      {/* Overall score hero */}
      <div className="fq-audit-overall">
        <div className="fq-audit-overall-grid">
          <div>
            <span className="fq-audit-overall-eyebrow">Your Money Mindset Profile</span>
            <div className="fq-audit-total" data-testid="fa-grand-total">
              {result.grand_total}<span className="fq-audit-total-max">/{result.max_total}</span>
            </div>
          </div>
          <div>
            <h1 className="fq-audit-overall-label" data-testid="fa-overall-label">{result.overall_label}</h1>
            <p className="fq-audit-overall-interp">{result.overall_interp}</p>
          </div>
        </div>
      </div>

      {/* Primary block */}
      <div className="fq-primary-block" data-testid="fa-primary-block">
        <span className="fq-primary-block-eyebrow">Primary block — start here</span>
        <h2 className="fq-primary-block-title">
          {result.primary_block.title} ({result.primary_block.score}/20)
        </h2>
        <p className="fq-primary-block-interp">
          {result.primary_block.band_label}
          {result.primary_block.band_interp && ` — ${result.primary_block.band_interp}`}
        </p>
      </div>

      {/* 5-belief scoreboard */}
      <div className="fq-results-body" style={{ paddingBottom: 28 }}>
        <h3 className="fq-section-title">Your 5 Belief Scores</h3>
        <p className="fq-pillar-feedback" style={{ marginBottom: 22, fontSize: 14.5 }}>
          The category with the highest score is your primary block — that's the one quietly running the show.
          Lower scores mean a healthier belief in that area.
        </p>
        <div className="fq-audit-rows">
          {result.breakdown.map((b) => {
            const bucket = bucketForBelief(b.score);
            const pct = (b.score / 20) * 100;
            return (
              <div className="fq-audit-row" key={b.key} data-testid={`fa-row-${b.key}`}>
                <div className={`fq-audit-row-pill bucket-${bucket}`}>
                  {bucket === 0 && "Strong"}
                  {bucket === 1 && "Mixed"}
                  {bucket === 2 && "Limiting"}
                </div>
                <div>
                  <h4 className="fq-audit-row-title">#{b.id} {b.title}</h4>
                  <div className="fq-audit-row-bar">
                    <div className={`fq-audit-row-bar-fill bucket-${bucket}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="fq-audit-row-score">{b.score}/20</div>
              </div>
            );
          })}
        </div>

        {result.email_status === "success" && (
          <div className="fq-confirmation" data-testid="fa-email-confirm" style={{ marginTop: 14 }}>
            <Mail size={16} /> A copy of your full audit (with all 5 reframe exercises) has been sent to&nbsp;
            <strong style={{ color: "var(--navy)" }}>{lead.email}</strong>
          </div>
        )}
      </div>

      {/* Reframe cards (primary block first) */}
      <div style={{ marginBottom: 24 }}>
        <h3 className="fq-section-title" style={{ paddingLeft: 4 }}>Your Reframe Exercises</h3>
        <p className="fq-pillar-feedback" style={{ marginBottom: 18, paddingLeft: 4, fontSize: 14.5 }}>
          Awareness is step one. Now use these reframes — start with the primary block and build from there.
        </p>
        {orderedReframes.map((b) => (
          <ReframeCard key={b.key} belief={b} isPrimary={b.key === primaryKey} />
        ))}
      </div>

      {/* CTA card with full sales pitch */}
      <div className="fq-cta-card" data-testid="fa-cta-card">
        <div className="fq-cta-kicker">Your Next Step</div>
        <h3 className="fq-cta-headline">
          Ready to actually rewire these beliefs and rebuild your financial foundation?
        </h3>
        <p className="fq-cta-sub">
          These beliefs didn't form overnight — and they won't shift with one worksheet.
          Most people stop here. They have the insight but no implementation plan. You don't have to be most people.
        </p>

        <div className="fq-pitch-card">
          <div className="fq-pitch-kicker">The Financial Foundation Reset™</div>
          <h4 className="fq-pitch-headline">
            Build a budget that actually works — and finally make your money support the life you want.
          </h4>
          <p className="fq-pitch-copy">
            <strong>The Financial Foundation Reset™</strong> doesn't just change how you manage money — it changes
            how you experience your life, because money stops being the obstacle and starts becoming a tool.
          </p>
          <div className="fq-pitch-points">
            <div className="fq-pitch-point">Daily videos to rewire your money mindset</div>
            <div className="fq-pitch-point">A proven, step-by-step budgeting system</div>
            <div className="fq-pitch-point">Live Zoom support + private community</div>
          </div>
          <p className="fq-pitch-copy">
            When a strong money mindset is paired with a practical budgeting system, taking control of your money —
            and building lasting financial success — becomes <strong>inevitable</strong>.
          </p>
          <p className="fq-pitch-copy">
            This six-week, guided experience helps you feel <strong>calm, confident, and in control</strong> of your money.
          </p>
          <p className="fq-pitch-copy" style={{ marginBottom: 0 }}>
            You don't need more advice. You need a foundation. And this is where it begins.
          </p>
        </div>

        <a href={RESET_CTA_URL} target="_blank" rel="noopener noreferrer"
           className="fq-btn fq-btn-gold" data-testid="fa-cta-button">
          <ShieldCheck size={18} />
          Click here → Financial Foundation Reset
        </a>
        <div className="fq-cta-foot">Awareness. Reframe. Rewire.</div>
      </div>

      {/* Education disclaimer */}
      <div className="fq-disclaimer" data-testid="fa-disclaimer">
        <div className="fq-disclaimer-icon"><Info size={18} strokeWidth={2.5} /></div>
        <div>
          <strong>This is financial education — not financial advice.</strong>
          <p style={{ margin: "4px 0 0", fontSize: 13.5, lineHeight: 1.65 }}>
            The audit, scores, and reframes above are general educational tools based on your self-reported answers.
            They are not personalized to your full financial picture. For decisions involving taxes, investments,
            insurance, estate planning, or any other specific aspect of your finances, please consult a qualified,
            licensed professional (CPA, CFP®, attorney, or licensed insurance agent) before taking action.
          </p>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button className="fq-btn fq-btn-ghost" onClick={onRestart} data-testid="fa-restart">
          <RefreshCw size={14} /> Take the audit again
        </button>
      </div>
    </div>
  );
}

function ReframeCard({ belief, isPrimary }) {
  const r = belief.reframe || {};
  return (
    <div className={`fq-reframe-card ${isPrimary ? "primary" : ""}`} data-testid={`fa-reframe-${belief.key}`}>
      <div className="fq-reframe-card-head">
        <h4 className="fq-reframe-card-title">
          {isPrimary && <span style={{ color: "var(--gold)", marginRight: 8, fontSize: 14, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 800, fontFamily: "Manrope, sans-serif" }}>Start here →</span>}
          #{belief.id} {belief.title}
        </h4>
        <span className="fq-reframe-card-score">{belief.score}/20 · {belief.band_label}</span>
      </div>
      <p className="fq-reframe-card-tag">{belief.tagline}</p>

      <div className="fq-reframe-block">
        <p className="fq-reframe-section-label">The Reframe Exercise</p>
        <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--ink)", margin: "0 0 12px" }}>{r.intro}</p>
        <div className="fq-reframe-prompt">{r.old_prompt}</div>
      </div>

      <div className="fq-reframe-block">
        <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--ink)", margin: "0 0 10px", whiteSpace: "pre-line" }}>{r.new_prompt}</p>
        <div className="fq-reframe-example">{r.example}</div>
      </div>

      <div className="fq-reframe-block">
        <p className="fq-reframe-section-label">This week's practice</p>
        <div className="fq-reframe-practice">{r.practice}</div>
      </div>
    </div>
  );
}
